import { useEffect, useRef, useState } from "react";
import { TbLayoutGrid, TbList, TbPlus, TbSearch } from "react-icons/tb";
import intl from "react-intl-universal";
import { ScreenGreeting } from "../../../Components/Molecules";
import { OrganizersSkeleton } from "../../../Components/Molecules/DashboardSkeleton";
import { useAuth } from "../../../Contexts/AuthContext";
import { useSubAccountsList } from "../../../Hooks/useSubAccounts";
import { OrganizerCard } from "./Card";
import { normalizeSearchText } from "./highlightSearchText";
import OrganizerListTable from "./ListTable";
import { OrganizerForm, OrginizerFormRef } from "./OrganizerForm";

type ViewMode = "grid" | "list";

const VIEW_MODE_KEY = "@Organizers:viewMode";

export function Organizers() {
  const organizerFormRef = useRef<OrginizerFormRef>(null);
  const { hasPermission } = useAuth();

  const {
    handleGET: fetchSubAccountsGET,
    loading,
    state: subAccounts,
  } = useSubAccountsList();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_KEY);
      return stored === "grid" || stored === "list" ? (stored as ViewMode) : "grid";
    } catch {
      return "grid";
    }
  });

  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchSubAccountsGET({});
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      // ignore storage errors
    }
  }, [viewMode]);

  const filteredSubAccounts = Array.isArray(subAccounts)
    ? subAccounts.filter((item) => {
        const haystack = normalizeSearchText(
          [
            item.name,
            item.idCustomer,
            item.subAccountId,
            item.isActive ? "ativo" : "inativo",
            String(item.available ?? ""),
          ].join(" ")
        );
        const needle = normalizeSearchText(search);
        return needle.length === 0 || haystack.includes(needle);
      })
    : [];

  const canCreate = hasPermission("admin");
  const isLoading = loading.GET;
  const emptyMessage = search
    ? `Nenhum resultado para "${search}".`
    : "Não há ponto de venda cadastrado.";

  if (isLoading) {
    return (
      <div className="profile-page w-full min-w-0 max-w-full">
        <ScreenGreeting
          title={intl.get("words.organizers")}
          subTitle="Faça o gerenciamento dos seus pontos de venda com facilidade."
        />
        <OrganizersSkeleton />
      </div>
    );
  }

  return (
    <div className="profile-page w-full min-w-0 max-w-full">
      <ScreenGreeting
        title={intl.get("words.organizers")}
        subTitle="Faça o gerenciamento dos seus pontos de venda com facilidade."
      />

      <OrganizerForm onSucess={() => fetchSubAccountsGET({})} ref={organizerFormRef} />

      {canCreate ? (
        <section className="mb-5">
          <p className="dashboard-section-label mb-3">Ações</p>
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            onClick={() =>
              organizerFormRef.current?.openOrganizer({
                name: "",
                subAccountId: "",
                isActive: true,
              })
            }
          >
            <TbPlus className="h-4 w-4" />
            Criar ponto de venda
          </button>
        </section>
      ) : null}

      <section className="min-w-0">
        <p className="dashboard-section-label mb-3">Pontos de venda</p>

        <div className="app-card overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between">
            <div className="relative min-w-0 flex-1">
              <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Digite para filtrar..."
                aria-label="Buscar pontos de venda"
                className="valut-input h-10 w-full rounded-xl pl-9 pr-3"
              />
            </div>

            <div
              className="inline-flex shrink-0 self-end rounded-full border border-gray-200 bg-gray-50 p-1 dark:border-brand-dark-border dark:bg-[#1f1f1f] sm:self-auto"
              role="group"
              aria-label="Modo de visualização"
            >
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  viewMode === "grid"
                    ? "bg-white text-brand-dark shadow-sm dark:bg-brand-dark-elevated dark:text-gray-100"
                    : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
                }`}
                aria-pressed={viewMode === "grid"}
              >
                <TbLayoutGrid className="h-4 w-4" />
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  viewMode === "list"
                    ? "bg-white text-brand-dark shadow-sm dark:bg-brand-dark-elevated dark:text-gray-100"
                    : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
                }`}
                aria-pressed={viewMode === "list"}
              >
                <TbList className="h-4 w-4" />
                Lista
              </button>
            </div>
          </div>

          {viewMode === "grid" ? (
            filteredSubAccounts.length === 0 ? (
              <div className="app-empty-state m-4">{emptyMessage}</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filteredSubAccounts.map((subAccount) => (
                  <OrganizerCard
                    key={subAccount.subAccountId}
                    name={subAccount.name}
                    idCustomer={subAccount.idCustomer}
                    totalValue={subAccount.available}
                    isActive={subAccount.isActive}
                    subAccountId={subAccount.subAccountId}
                    highlight={search}
                    canNavigate={canCreate}
                  />
                ))}
              </div>
            )
          ) : (
            <OrganizerListTable
              data={filteredSubAccounts}
              emptyMessage={emptyMessage}
              canNavigate={canCreate}
              highlight={search}
            />
          )}
        </div>
      </section>
    </div>
  );
}
